





























































import {Component, Emit, Prop, Ref, Vue, Watch} from 'vue-property-decorator'
import {SceneShot, Shot, StillShot, TitleShot, VideoShot} from '@/store/shots'
import ShotThumbnailImage from '@/components/VideoTimeline/ShotThumbnailImage.vue'
import {projectStore, shotStore} from '@/store'
import {debounce} from 'lodash'

@Component({
  components: {ShotThumbnailImage}
})
export default class ShotThumbnail extends Vue {
  @Prop() shot!: StillShot | VideoShot | SceneShot | TitleShot
  @Prop() current!: boolean
  @Prop() scale!: number
  @Ref() trimmerArea!: HTMLDivElement
  @Ref() inTrimmer!: Vue
  @Ref() outTrimmer!: Vue

  private readonly oneRem = 16
  private readonly oneSecToPx = 30
  private readonly headerHeight = 74
  private readonly footerHeight = 46
  private readonly trimmerHeight = 20

  trimmer: {inPos: number; outPos: number; which: 'in' | 'out' | false; prevScreenY: number} = {
    inPos: 0,
    outPos: 0,
    which: false,
    prevScreenY: 0,
  }

  mounted() {
    this.updateTrimmers()
  }

  @Watch('scale')
  scaleWatch(newScale: number, oldScale: number) {
    if (newScale !== oldScale)
      this.updateTrimmers()
  }

  @Watch('shot.inPoint')
  updateInTrimmer(newInPoint: number, oldInPoint: number) {
    if (newInPoint !== oldInPoint)
      this.updateTrimmerInPos()
  }

  @Watch('shot.outPoint')
  updateOutTrimmer(newOutPoint: number, oldOutPoint: number) {
    if (newOutPoint !== oldOutPoint)
      this.updateTrimmerOutPos()
  }

  @Emit('shot-selected')
  onClick(shot: StillShot | VideoShot | SceneShot | TitleShot) {
    return shot
  }

  private updateTrimmerInPos() {
    const shot = this.shot
    this.trimmer.inPos = shot.inPoint * this.oneSecToPx * this.scale
  }

  private updateTrimmerOutPos() {
    const shot = this.shot
    this.trimmer.outPos = shot.outPoint * this.oneSecToPx * this.scale + this.trimmerHeight
  }

  private updateTrimmers() {
    this.updateTrimmerInPos()
    this.updateTrimmerOutPos()
  }

  get heightInRem(): string {
    if (this.current) {
      return `${this.activeShotHeight / this.oneRem}rem`
    } else {
      const height = this.inactiveShotHeight
      return height / this.oneRem >= 6.5 ? `${height / this.oneRem}rem` : '6.5rem'
    }
  }

  get inactiveShotHeight(): number {
    const shotDuration = this.shotTrimmedDuration
    const shotHeight = shotDuration * this.oneSecToPx

    return shotHeight * this.scale
  }

  get activeShotHeight(): number {
    return this.headerHeight + this.trimmerAreaHeight + this.footerHeight
  }

  get trimmerAreaHeight(): number {
    const totalShotDuration = this.shot.durationInSec
    const trimmerAreaHeight = totalShotDuration * this.oneSecToPx
    const trimmersHeight = this.trimmerHeight * 2

    return trimmersHeight + trimmerAreaHeight * this.scale
  }

  get trimmerAreaHeightInRem(): string {
    return `${this.trimmerAreaHeight / this.oneRem}rem`
  }

  get shotTrimmedDuration(): number {
    return Shot.getTrimmedDurInSec(this.shot)
  }

  get thumbnailImageFile(): string {
    return Shot.getThumbnailImageFile(this.shot)
  }

  get inPoint() {
    return this.trimmer.inPos / this.oneSecToPx / this.scale
  }

  get outPoint() {
    return (this.trimmer.outPos - this.trimmerHeight) / this.oneSecToPx / this.scale
  }

  @Emit('delete')
  onDelete() {
    return this.shot
  }

  startTrimming(ev: PointerEvent, which: 'in' | 'out') {
    this.trimmer.which = which
    if (which === 'in')
      this.inTrimmer.$el.setPointerCapture(ev.pointerId)
    else
      this.outTrimmer.$el.setPointerCapture(ev.pointerId)
  }

  moveTrimmer(ev: PointerEvent) {
    const trimmer = this.trimmer
    const movementY = ev.screenY - trimmer.prevScreenY
    if (trimmer.which && movementY !== 0) {
      const pos = trimmer.which === 'in' ? trimmer.inPos : trimmer.outPos
      let translateY = pos + movementY
      let localTime

      if (trimmer.which === 'in') {
        if (translateY <= 0)
          translateY = 0
        else if (translateY >= trimmer.outPos - this.trimmerHeight)
          translateY = trimmer.outPos - this.trimmerHeight
        trimmer.inPos = translateY
        localTime = this.inPoint
        shotStore.setShotInPoint({shot: this.shot, inPoint: this.inPoint})
      } else {
        if (translateY >= this.trimmerAreaHeight - this.trimmerHeight)
          translateY = this.trimmerAreaHeight - this.trimmerHeight
        else if (translateY <= trimmer.inPos + this.trimmerHeight)
          translateY = trimmer.inPos + this.trimmerHeight
        this.trimmer.outPos = translateY
        localTime = this.outPoint
        shotStore.setShotOutPoint({shot: this.shot, outPoint: this.outPoint})
      }
      // TODO: this is needed to seek video, but triggers project save very frequently
      projectStore.setLocalTime(localTime)
    }
    trimmer.prevScreenY = ev.screenY
  }

  stopTrimming(ev: PointerEvent) {
    if (this.trimmer.which) {
      if (this.trimmer.which === 'in')
        this.inTrimmer.$el.releasePointerCapture(ev.pointerId)
      else
        this.outTrimmer.$el.releasePointerCapture(ev.pointerId)
      this.trimmer.which = false
      shotStore.requestSave({shot: this.shot, path: []})
    }
  }
}
